import { api } from './api';

export interface TerritoryFeature {
    type: 'Feature';
    properties: {
        county_fips: string;
        county_name: string;
        display_name?: string;
        custom_name?: string;
        state_name: string;
        team_id: number;
        team_name: string;
        primary_color: string;
        secondary_color: string | null;
        map_color?: string | null;
        fill_color?: string; // The assigned color (map_color, primary, or secondary based on adjacency)
        logo_url?: string | null;
        population?: number | null;
    };
    geometry: {
        type: 'Polygon' | 'MultiPolygon';
        coordinates: number[][][] | number[][][][];
    };
}

export interface TerritoryGeoJSON {
    type: 'FeatureCollection';
    features: TerritoryFeature[];
}

export interface TeamConquestInfo {
    winner_name: string | null;
    loser_name: string | null;
    winner_score: number | null;
    loser_score: number | null;
    /** Fantasy leagues: the winning side's top starter that week. */
    winner_top_player?: string | null;
    winner_top_points?: number | null;
    game_date: string | null;
}

export interface ConquestChainEntry {
    conquest_id?: number;
    date?: string | null;
    previous_owner_team_id?: number | null;
    previous_owner_name?: string | null;
    previous_owner_color?: string | null;
    previous_owner_map_color?: string | null;
    previous_owner_logo?: string | null;
    previous_owner_espn_team_id?: number | null;
    new_owner_team_id?: number;
    new_owner_name?: string;
    new_owner_color?: string;
    new_owner_map_color?: string;
    new_owner_logo?: string;
    new_owner_espn_team_id?: number | null;
    game_id?: number | null;
    game_date?: string | null;
    winner_name?: string | null;
    loser_name?: string | null;
    winner_score?: number | null;
    loser_score?: number | null;
    is_initial: boolean;
    team_id?: number;
    team_name?: string;
    primary_color?: string;
    map_color?: string;
    logo_url?: string;
    espn_team_id?: number | null;
    distance_km?: number;
    chain_break?: boolean; // Indicates if this entry has a chain break
}

export interface ChainBreak {
    index?: number;
    conquest_id?: number;
    expected_previous_owner?: number;
    actual_previous_owner?: number;
    expected_current_owner?: number;
    actual_current_owner?: number;
    conquest_date?: string;
    type?: string;
    message: string;
}

export interface ConquestChainResponse {
    success: boolean;
    county_fips: string;
    county_name: string | null;
    state_name: string | null;
    population?: number | null;
    history: ConquestChainEntry[];
    current_owner: {
        team_id: number;
        team_name: string;
        primary_color: string;
        map_color: string | null;
        logo_url: string | null;
        espn_team_id: number | null;
        last_conquest_date: string | null;
    } | null;
    chain_breaks?: ChainBreak[];
    chain_valid?: boolean;
    error?: string;
}

export const territoryService = {
    getTerritoryGeoJSON: async (leagueId: string, date?: string, view?: string, season?: number): Promise<TerritoryGeoJSON> => {
        const params = new URLSearchParams();
        if (date) params.append('date', date);
        if (view) params.append('view', view);
        if (season != null) params.append('season', String(season));
        const queryString = params.toString();
        const url = queryString 
            ? `/territories/${leagueId}/geojson?${queryString}`
            : `/territories/${leagueId}/geojson`;
        const response = await api.get<TerritoryGeoJSON>(url);
        if (response.success && response.data) {
            return response.data as TerritoryGeoJSON;
        }
        throw new Error(response.error || 'Failed to fetch territory GeoJSON');
    },

    getTeamConquests: async (
        leagueId: string,
        teamId: number,
        date?: string,
        season?: number
    ): Promise<Record<string, TeamConquestInfo>> => {
        const params = new URLSearchParams();
        params.append('teamId', String(teamId));
        if (date) params.append('date', date);
        if (season != null) params.append('season', String(season));
        const response = await api.get<{ success: boolean; conquests: Record<string, TeamConquestInfo> }>(
            `/territories/${leagueId}/conquests?${params.toString()}`
        );
        if (response.success && response.data) {
            return response.data.conquests || {};
        }
        return {};
    },

    /** Season's original owner of every county: county_fips -> { team_id, color }. */
    getInitialOwners: async (
        leagueId: string,
        season?: number
    ): Promise<Map<string, { team_id: number; color: string }>> => {
        const params = new URLSearchParams();
        if (season != null) params.append('season', String(season));
        const qs = params.toString();
        const response = await api.get<{ success: boolean; owners: Array<{ county_fips: string; team_id: number | string; color: string }> }>(
            `/territories/${leagueId}/initial-owners${qs ? `?${qs}` : ''}`
        );
        const map = new Map<string, { team_id: number; color: string }>();
        if (response.success && response.data) {
            (response.data.owners || []).forEach((o) => {
                map.set(o.county_fips, { team_id: Number(o.team_id), color: o.color });
            });
        }
        return map;
    },

    getConquestChain: async (leagueId: string, countyFips: string): Promise<ConquestChainResponse> => {
        // Prefer the county detail endpoint (includes full history).
        const detail = await territoryService.getCountyDetail(leagueId, countyFips);
        return {
            success: true,
            county_fips: detail.county.county_fips,
            county_name: detail.county.county_name,
            state_name: detail.county.state_name,
            population: detail.county.population,
            history: (detail.history || []).map((h) => ({
                conquest_id: h.conquest_id,
                game_id: h.game_id,
                game_date: h.game_date,
                previous_owner_team_id: h.previous_owner_team_id,
                previous_owner_name: h.previous_owner_name,
                new_owner_team_id: h.new_owner_team_id,
                new_owner_name: h.new_owner_name || undefined,
                winner_name: h.winner_name,
                loser_name: h.loser_name,
                winner_score: h.winner_score,
                loser_score: h.loser_score,
                is_initial: false,
            })),
            current_owner: detail.current_owner
                ? {
                      team_id: detail.current_owner.team_id,
                      team_name: detail.current_owner.team_name,
                      primary_color: detail.current_owner.primary_color || '#888',
                      map_color: detail.current_owner.map_color,
                      logo_url: detail.current_owner.logo_url,
                      espn_team_id: detail.current_owner.espn_team_id,
                      last_conquest_date: detail.current_owner.last_conquest_date,
                  }
                : null,
        };
    },

    getCountyDetail: async (
        leagueId: string,
        countyFips: string,
        opts?: { date?: string; season?: number | string }
    ): Promise<CountyDetailResponse> => {
        const params = new URLSearchParams();
        if (opts?.date) params.append('date', opts.date);
        if (opts?.season != null) params.append('season', String(opts.season));
        const qs = params.toString();
        const fips = String(countyFips).padStart(5, '0');
        const response = await api.get<CountyDetailResponse>(
            `/territories/${leagueId}/county/${fips}${qs ? `?${qs}` : ''}`
        );
        if (response.success && response.data) {
            return response.data as CountyDetailResponse;
        }
        throw new Error(response.error || 'Failed to fetch county detail');
    },

    getTeamTidbits: async (leagueId: string, teamId: number): Promise<TeamEmpireTidbits> => {
        const cacheKey = `${leagueId}:${teamId}`;
        const hit = tidbitsCache.get(cacheKey);
        if (hit && Date.now() - hit.at < TIDBITS_CACHE_MS) {
            return hit.data;
        }
        const response = await api.get<TeamEmpireTidbits>(
            `/territories/${leagueId}/team/${teamId}/tidbits`
        );
        if (response.success && response.data) {
            const data = response.data as TeamEmpireTidbits;
            tidbitsCache.set(cacheKey, { at: Date.now(), data });
            return data;
        }
        throw new Error(response.error || 'Failed to fetch team tidbits');
    },
};

const tidbitsCache = new Map<string, { at: number; data: TeamEmpireTidbits }>();
const TIDBITS_CACHE_MS = 5 * 60_000;

export interface TeamEmpireSwing {
    counties: number;
    date: string | null;
    season_year: number | null;
    foe_team_id: number | null;
    foe_name: string | null;
    foe_short: string | null;
    winner_score: number | null;
    loser_score: number | null;
}

export interface TeamEmpireTidbits {
    success?: boolean;
    league_id?: string;
    team_id?: number;
    peak_counties: {
        counties: number;
        date: string | null;
        season_year: number | null;
    } | null;
    biggest_loss: TeamEmpireSwing | null;
    biggest_win: TeamEmpireSwing | null;
}

export interface CountyDetailConquest {
    conquest_id: number;
    game_id: number | null;
    conquest_date: string | null;
    game_date: string | null;
    previous_owner_team_id: number | null;
    previous_owner_name: string | null;
    previous_owner_color?: string | null;
    previous_owner_logo?: string | null;
    previous_owner_espn_team_id?: number | null;
    new_owner_team_id: number;
    new_owner_name: string | null;
    new_owner_color?: string | null;
    new_owner_logo?: string | null;
    new_owner_espn_team_id?: number | null;
    winner_name: string | null;
    loser_name: string | null;
    winner_score: number | null;
    loser_score: number | null;
    winner_top_player?: string | null;
    winner_top_points?: number | null;
}

export interface CountyDetailResponse {
    success: boolean;
    league_id: string;
    season: number | string;
    date: string | null;
    county: {
        county_fips: string;
        county_name: string;
        state_name: string;
        state_fips?: string;
        population: number;
    };
    current_owner: {
        team_id: number;
        team_name: string;
        short_name?: string | null;
        primary_color: string | null;
        map_color: string | null;
        logo_url: string | null;
        espn_team_id: number | null;
        last_conquest_date: string | null;
    } | null;
    initial_owner: {
        team_id: number;
        team_name: string;
        short_name?: string | null;
        primary_color: string | null;
        map_color: string | null;
        logo_url?: string | null;
        espn_team_id?: number | null;
    } | null;
    is_home_turf: boolean | null;
    latest_conquest: CountyDetailConquest | null;
    history: CountyDetailConquest[];
    times_flipped: number;
    days_held: number | null;
    owner_population: number | null;
    county_share_of_owner_pop: number | null;
}

